import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ShieldCheck, ChevronDown, ChevronRight, Hash } from "lucide-react";
import { format, subDays, differenceInDays } from "date-fns";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/Select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { DateRangePicker } from "@/components/ui/DatePicker";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";
import { useTranslation } from "@/hooks/useTranslation";
import {
  auditActionLabel,
  auditResourceLabel,
  AUDIT_FILTER_ACTIONS,
  AUDIT_FILTER_RESOURCES,
} from "@/lib/audit-labels";

// ─────────────────────────────────────────────
// AuditLogs
//
// Screen — /audit-logs
// Filters: date range (max 90 days), resource type, action, actor.
// DataTable with expandable rows + hash chain verification modal.
// Design: §8.5 Audit Logs
// ─────────────────────────────────────────────

// Raw shape returned by the API
interface AuditLogApiRow {
  id: string;
  timestamp: string | Date;
  actorId: string;
  actorType: string;
  action: string;
  resourceType: string;
  resourceId: string;
  ipAddress: string | null;
  contentHash: string;
  previousState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
}

// Normalised shape used by the UI
interface AuditLogRow {
  id: string;
  timestamp: string;
  actor: string;
  actorId: string;
  action: string;
  resourceType: string;
  resourceName: string;
  resourceId: string;
  ip: string;
  hash: string;
  diff?: Record<string, unknown> | null;
}

interface AuditLogsApiResponse {
  data: AuditLogApiRow[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

interface AuditLogsResponse {
  data: AuditLogRow[];
  meta: {
    nextCursor: string | null;
    hasMore: boolean;
  };
}

function normalizeRow(row: AuditLogApiRow): AuditLogRow {
  return {
    id: row.id,
    timestamp: typeof row.timestamp === 'string' ? row.timestamp : new Date(row.timestamp).toISOString(),
    actor: row.actorId,
    actorId: row.actorId,
    action: row.action,
    resourceType: row.resourceType,
    resourceName: row.resourceId,
    resourceId: row.resourceId,
    ip: row.ipAddress ?? '',
    hash: row.contentHash,
    diff: (row.previousState || row.newState)
      ? { before: row.previousState, after: row.newState }
      : null,
  };
}

interface HashVerifyResult {
  valid: boolean;
  checkedCount: number;
  firstBroken?: string;
}

// Action badge color map
function ActionBadge({ action }: { action: string }) {
  const { t } = useTranslation();
  const config = useMemo(() => {
    const a = action.toUpperCase();
    if (a.includes("CREATE")) return { bg: "bg-success-bg", text: "text-success" };
    if (a.includes("UPDATE") || a.includes("PATCH")) return { bg: "bg-accent-bg", text: "text-accent-text" };
    if (a.includes("DELETE") || a.includes("REMOVE")) return { bg: "bg-danger-bg", text: "text-danger" };
    if (a.includes("LOGIN") || a.includes("AUTH")) return { bg: "bg-k-purple-bg", text: "text-k-purple" };
    return { bg: "bg-hover", text: "text-text-muted" };
  }, [action]);

  return (
    <span className={cn("inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold", config.bg, config.text)}>
      {auditActionLabel(t, action)}
    </span>
  );
}

// Renders the actor column: "You" if current user, then the resolved display name,
// falling back to a truncated UUID with the full ID in a tooltip.
function ActorCell({
  actorId,
  currentUserId,
  nameById,
}: {
  actorId: string;
  currentUserId?: string;
  nameById: Map<string, string>;
}) {
  const { t } = useTranslation();
  if (actorId === currentUserId) {
    return <span className="text-body text-text-primary font-medium">{t("auditLogs.you")}</span>;
  }
  const name = nameById.get(actorId);
  if (name) {
    return (
      <span className="text-body text-text-secondary" title={actorId}>
        {name}
      </span>
    );
  }
  // No known name — show first 8 chars + ellipsis with full ID in tooltip
  const short = actorId.length > 12 ? `${actorId.slice(0, 8)}…` : actorId;
  return (
    <span
      className="font-mono text-[12px] text-text-secondary cursor-help"
      title={actorId}
    >
      {short}
    </span>
  );
}

function ExpandableRow({
  row,
  currentUserId,
  nameById,
}: {
  row: AuditLogRow;
  currentUserId?: string;
  nameById: Map<string, string>;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="border-b border-border-subtle cursor-pointer hover:bg-hover last:border-0"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="w-8 px-3 py-3.5">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-text-muted" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-text-muted" />
          )}
        </td>
        <td className="px-3 py-3.5">
          <span className="font-mono text-[12px] text-text-secondary">
            {format(new Date(row.timestamp), "yyyy-MM-dd HH:mm:ss")}
          </span>
        </td>
        <td className="px-3 py-3.5">
          <ActorCell actorId={row.actorId} currentUserId={currentUserId} nameById={nameById} />
        </td>
        <td className="px-3 py-3.5">
          <ActionBadge action={row.action} />
        </td>
        <td className="px-3 py-3.5">
          <span className="text-body text-text-secondary">{auditResourceLabel(t, row.resourceType)}</span>
        </td>
        <td className="px-3 py-3.5">
          <span className="text-body text-text-secondary">{row.resourceName}</span>
        </td>
        <td className="px-3 py-3.5">
          <span className="font-mono text-[12px] text-text-muted">{row.ip}</span>
        </td>
        <td className="px-3 py-3.5">
          <span
            className="font-mono text-[11px] text-text-muted"
            title={row.hash}
          >
            {row.hash.slice(0, 8)}…
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border-subtle bg-hover">
          <td colSpan={8} className="px-6 py-3">
            <div>
              <p className="mb-1 text-caption font-medium text-text-muted uppercase tracking-wider">
                {t("auditLogs.fullHash")}
              </p>
              <p className="mb-3 font-mono text-[12px] text-text-secondary break-all">{row.hash}</p>
              {row.diff && (
                <>
                  <p className="mb-1 text-caption font-medium text-text-muted uppercase tracking-wider">
                    {t("auditLogs.changes")}
                  </p>
                  <pre className="overflow-auto rounded-control border border-border-default bg-bg-input p-3 text-[12px] text-text-secondary max-h-40">
                    {JSON.stringify(row.diff, null, 2)}
                  </pre>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function VerifyHashModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [result, setResult] = useState<HashVerifyResult | null>(null);

  const mutation = useMutation({
    mutationFn: async (range: { startDate: string; endDate: string }) => {
      const res = await api.post<{ data: { valid: boolean; entriesChecked: number; brokenAt?: string } }>(
        "/audit-logs/verify",
        range,
      );
      // Normalise API response to the shape this component uses
      return {
        valid: res.data.valid,
        checkedCount: res.data.entriesChecked,
        firstBroken: res.data.brokenAt,
      } as HashVerifyResult;
    },
    onSuccess: (data) => setResult(data),
    onError: (err: Error) => toast.error(err.message),
  });

  function handleVerify() {
    if (!dateRange.from || !dateRange.to) {
      toast.error(t("auditLogs.selectDateRange"));
      return;
    }
    const diff = differenceInDays(dateRange.to, dateRange.from);
    if (diff > 90) {
      toast.error(t("auditLogs.dateRangeTooLong"));
      return;
    }
    mutation.mutate({
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-accent" />
            {t("auditLogs.verifyHashChain")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <p className="text-body text-text-muted">
            {t("auditLogs.verifyHashDesc")}
          </p>
          <DateRangePicker
            value={dateRange}
            onChange={(r) => r?.from && r?.to && setDateRange({ from: r.from, to: r.to })}
            maxDays={90}
          />

          {result && (
            <div className={cn(
              "rounded-card border p-4",
              result.valid ? "border-success/30 bg-success-bg" : "border-danger/30 bg-danger-bg"
            )}>
              {result.valid ? (
                <div className="flex items-center gap-2 text-success">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="font-medium">{t("auditLogs.hashValid")}</span>
                  <span className="text-caption">({result.checkedCount} {t("auditLogs.entriesVerified")})</span>
                </div>
              ) : (
                <div className="text-danger">
                  <p className="font-medium">{t("auditLogs.hashBroken")}</p>
                  {result.firstBroken && (
                    <p className="mt-1 text-caption">{t("auditLogs.firstBroken")}: {result.firstBroken}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>
          <Button onClick={handleVerify} isLoading={mutation.isPending}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {t("auditLogs.verify")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AuditLogs() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  // Role name comes from UserProfile.role.name — stored as-is (e.g., "Org Admin")
  const canVerifyChain = userCan(user, "read", "AuditLog");

  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [resourceType, setResourceType] = useState("");
  const [action, setAction] = useState("");
  const [searchParams] = useSearchParams();
  const [actor, setActor] = useState(searchParams.get("actorId") ?? "");
  const [cursor, setCursor] = useState<string | null>(null);
  const [allRows, setAllRows] = useState<AuditLogRow[]>([]);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const params = new URLSearchParams({ pageSize: "50" });
  // API expects ISO 8601 datetime strings for startDate/endDate
  if (dateRange.from) params.set("startDate", dateRange.from.toISOString());
  if (dateRange.to) params.set("endDate", dateRange.to.toISOString());
  if (resourceType) params.set("resourceType", resourceType);
  if (action) params.set("action", action);
  // API param is actorId; the actor filter searches by ID in this schema
  if (actor) params.set("actorId", actor);
  // cursor stores the next page number as a string
  if (cursor) params.set("page", cursor);

  const { data, isLoading, isFetching } = useQuery<AuditLogsResponse>({
    queryKey: ["audit-logs", dateRange, resourceType, action, actor, cursor],
    // allRows is (re)populated as a side effect of queryFn below. With the global
    // 5-minute staleTime, toggling a filter back to a previously-fetched combination
    // served cached data WITHOUT re-running queryFn, leaving allRows stuck empty.
    // staleTime: 0 forces a refetch on every key (re)activation so allRows repopulates.
    staleTime: 0,
    queryFn: async () => {
      const res = await api.get<AuditLogsApiResponse>(`/audit-logs?${params.toString()}`);
      const normalized = res.data.map(normalizeRow);

      // Derive cursor-style pagination from the offset-based meta the API returns
      const currentPage = res.meta.page;
      const totalPages = res.meta.totalPages;
      const hasMore = currentPage < totalPages;
      // Encode the next page number as the cursor
      const nextCursor = hasMore ? String(currentPage + 1) : null;

      const normalizedResponse: AuditLogsResponse = {
        data: normalized,
        meta: { nextCursor, hasMore },
      };

      if (cursor === null) {
        setAllRows(normalized);
      } else {
        setAllRows((prev) => [...prev, ...normalized]);
      }
      return normalizedResponse;
    },
  });

  // Resolve actor IDs to display names, and drive the actor filter by name.
  // Read-only; reuses the existing users list endpoint. Degrades gracefully
  // (truncated ID) when the list is unavailable or a referenced user is absent.
  const { data: usersRes } = useQuery<{ data: Array<{ id: string; name: string }> }>({
    queryKey: ["users", "audit-actors"],
    queryFn: () => api.get("/users?pageSize=100"),
    staleTime: 5 * 60 * 1000,
  });

  const actorOptions = useMemo(
    () => (Array.isArray(usersRes?.data) ? usersRes.data : []),
    [usersRes],
  );

  const actorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of actorOptions) m.set(u.id, u.name);
    return m;
  }, [actorOptions]);

  function handleFilterChange() {
    setCursor(null);
    setAllRows([]);
  }

  function handleLoadMore() {
    if (data?.meta?.nextCursor) {
      setCursor(data.meta.nextCursor);
    }
  }

  return (
    <>
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-semibold text-text-primary">{t("auditLogs.title")}</h1>
          <p className="mt-1 text-body text-text-muted">
            {t("auditLogs.subtitle")}
          </p>
        </div>
        {canVerifyChain && (
          <Button variant="outline" onClick={() => setVerifyOpen(true)}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {t("auditLogs.verifyHashChain")}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="mb-1 text-[13px] text-text-secondary">{t("auditLogs.dateRange")}</p>
          <DateRangePicker
            value={dateRange}
            onChange={(r) => { if (r?.from && r?.to) { setDateRange({ from: r.from, to: r.to }); handleFilterChange(); } }}
            maxDays={90}
          />
        </div>

        <div className="w-44">
          <p className="mb-1 text-[13px] text-text-secondary">{t("auditLogs.resourceType")}</p>
          <SelectRoot value={resourceType} onValueChange={(v) => { setResourceType(v === "ALL" ? "" : v); handleFilterChange(); }}>
            <SelectTrigger>
              <SelectValue placeholder={t("auditLogs.allTypes")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("auditLogs.allTypes")}</SelectItem>
              {AUDIT_FILTER_RESOURCES.map((rt) => (
                <SelectItem key={rt} value={rt}>{auditResourceLabel(t, rt)}</SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>

        <div className="w-40">
          <p className="mb-1 text-[13px] text-text-secondary">{t("auditLogs.action")}</p>
          <SelectRoot value={action} onValueChange={(v) => { setAction(v === "ALL" ? "" : v); handleFilterChange(); }}>
            <SelectTrigger>
              <SelectValue placeholder={t("auditLogs.allActions")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("auditLogs.allActions")}</SelectItem>
              {AUDIT_FILTER_ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>{auditActionLabel(t, a)}</SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>

        <div className="w-48">
          <p className="mb-1 text-[13px] text-text-secondary">{t("auditLogs.actor")}</p>
          <SelectRoot value={actor} onValueChange={(v) => { setActor(v === "ALL" ? "" : v); handleFilterChange(); }}>
            <SelectTrigger>
              <SelectValue placeholder={t("auditLogs.allActors")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("auditLogs.allActors")}</SelectItem>
              {actorOptions.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-card border border-border-default bg-card">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-border-default">
                <th className="w-8 px-3 py-3" />
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.timestamp")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.actor")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.action")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.resourceType")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.resource")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.ip")}
                </th>
                <th className="px-3 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("auditLogs.hash")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && cursor === null ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-body text-text-muted">
                    {t("auditLogs.loading")}
                  </td>
                </tr>
              ) : allRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-body text-text-muted">
                    {t("auditLogs.noLogs")}
                  </td>
                </tr>
              ) : (
                allRows.map((row) => (
                  <ExpandableRow key={row.id} row={row} currentUserId={user?.id} nameById={actorNameById} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {data?.meta?.hasMore && (
          <div className="border-t border-border-subtle px-5 py-3 text-center">
            <Button variant="ghost" onClick={handleLoadMore} isLoading={isFetching && cursor !== null}>
              {t("common.loadMore")}
            </Button>
          </div>
        )}
      </div>

      <VerifyHashModal open={verifyOpen} onClose={() => setVerifyOpen(false)} />
    </>
  );
}
